import { Suspense } from "react";
import styled from "styled-components";
import { ErrorBoundary } from "react-error-boundary";
import { Flex } from "rebass";

import LoadingSVG from "../loading.svg";
import { URLS } from "../MVVM/Utilities/Maps";

const FallbackSVG = () => <StyledImage src={LoadingSVG} alt="loading" />;

const ErrorFallback = ({ resetErrorBoundary }) => {
  return (
    <div style={{ margin: "auto" }}>
      <Error>Failed to load data. Please reload the page and try again</Error>
      <button
        onClick={() => {
          resetErrorBoundary();
        }}
      >
        Try again
      </button>
    </div>
  );
};

export const MINIMAL_ROUTES = [
  "/referred",
  "/overwolf",
  "/allstar/gamersclub",
  "/mobile-app-link/terms-of-use",
  "/mobile-app-link/privacy-policy",
  "/mobile-app-link/login",
  "/mobile-app-link/signup",
  "/mobile-app-link/faq",
  "/login",
  "/signup",
  "/register",
  "/invite",
  "/reset",
  "/mobile-auth",
];

const LoadingPage = () => (
  <StyledLoadingPage>
    <FallbackSVG />
  </StyledLoadingPage>
);

const buildChunkable = (Component, extraProps) => (props) => (
  <ErrorBoundary FallbackComponent={ErrorFallback}>
    <Suspense fallback={<LoadingPage />}>
      <Component {...props} {...extraProps} />
    </Suspense>
  </ErrorBoundary>
);

/* Routes that spell `component={getChunkable(LazyThing)}` call this during
   every render of the route table. A fresh wrapper function is a different
   component *type* to React, so it unmounted the whole page and mounted a new
   one on any unrelated re-render — losing loaded pages, scroll position, and
   anything else held in page state. The wrapper is a pure function of props,
   so one per lazy component is all that's ever needed.

   Skipped when extraProps is passed: those objects are usually built inline
   and a stale capture would be worse than a remount. */
const chunkableCache = new WeakMap();

export const getChunkable = (Component, extraProps) => {
  if (extraProps) return buildChunkable(Component, extraProps);

  const cached = chunkableCache.get(Component);
  if (cached) return cached;

  const chunkable = buildChunkable(Component);
  chunkableCache.set(Component, chunkable);
  return chunkable;
};

const StyledImage = styled.img`
  max-width: 50px;
  margin: auto;
`;

const StyledLoadingPage = styled(Flex)`
  align-items: center;
  height: calc(100vh - 80px);
  justify-content: center;
  min-height: 400px;
`;
